import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import { Editor } from "@tinymce/tinymce-react";
import tinymceConfig from "../../../utils/tinymceConfig"; // adjust path


const MeetingCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [guests, setGuests] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assistantManagers, setAssistantManagers] = useState([]);
  const [overseers, setOverseers] = useState([]);
    const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtered, setFiltered] = useState({
      guests: [],
      managers: [],
      assistantManagers: [],
      overseers: []
    });
  
  const [form, setForm] = useState({
    title: '',
    agenda: '',
    notes: '',
    meeting_datetime: '',
    meeting_link: '',
    property_ids: [],
    guest_ids: [],
    manager_ids: [],
    assistant_manager_ids: [],
    staff_ids: [],
    overseer_ids: []
  });

  useEffect(() => {
        getProperties();
    getGuests();
    getManagers();
    getAssistantManagers();
    getOverseers();
  }, []);


  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-meeting-by-id/${id}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (res.data.status) setForm(res.data.meeting);
      })
      .catch(() => alert("Failed to fetch meeting details."));
  }, [id]);


   useEffect(() => {
      const selectedIds = form.property_ids.map(p => p.value);
  
      const filterByProperty = (list) =>
        list.filter(item => selectedIds.includes(item.property_id));
  
      setFiltered({
        guests: filterByProperty(guests),
        managers: filterByProperty(managers),
        assistantManagers: filterByProperty(assistantManagers),
        overseers: filterByProperty(overseers)
      });
    }, [form.property_ids, guests, managers, assistantManagers, overseers]);
  
    const getProperties = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-assigned-properties`, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        });
        if (res.data.status) {
          const propertyOptions = res.data.properties.map(p => ({
            value: p.id,
            label: p.property_name,
          }));
          setProperties(propertyOptions);
        }
      } catch (error) {
        globalErrorHandler(error, navigate);
      } finally {
        setIsLoading(false);
      }
    };
  
  
  const getGuests = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-guest-by-role-by-property`, {
        role: 'guest'
      }, {
        withCredentials: true
      });
      if (res.data.status) {
        const guestOptions = res.data.guests.map(g => ({
          value: g.id,
          label: `${g.name} ${g.email}`,
          property_id: g.property_id
        }));
        setGuests(guestOptions);
      }
    } catch (error) {
      globalErrorHandler(error, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  const getManagers = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-guest-by-role-by-property`, {
        role: 'manager'
      }, { withCredentials: true });
      const managerOptions = res.data.guests.map(g => ({
        value: g.id,
        label: `${g.name} ${g.email}`,
        property_id: g.property_id
      }));
      setManagers(managerOptions);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getAssistantManagers = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-guest-by-role-by-property`, {
        role: 'assistant_manager'
      }, { withCredentials: true });
      const assistantOptions = res.data.guests.map(g => ({
        value: g.id,
        label: `${g.name} ${g.email}`,
        property_id: g.property_id
      }));
      setAssistantManagers(assistantOptions);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getOverseers = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-guest-by-role-by-property`, {
        role: 'overseer'
      }, { withCredentials: true });
      const overseerOptions = res.data.guests.map(g => ({
        value: g.id,
        label: `${g.name} ${g.email}`,
        property_id: g.property_id
      }));
      setOverseers(overseerOptions);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };






  const updateMeeting = () => {
    const { title, meeting_datetime, agenda } = form;

    if (!title?.trim()) {
      Swal.fire("Validation Error", "Meeting title is required.", "warning");
      return;
    }

    if (!meeting_datetime) {
      Swal.fire("Validation Error", "Meeting date and time is required.", "warning");
      return;
    }

    const now = new Date();
    const selectedDate = new Date(meeting_datetime);
    now.setSeconds(0, 0);

    if (selectedDate < now) {
      Swal.fire("Invalid Date", "Meeting date/time must be today or in the future.", "warning");
      return;
    }

    if (!agenda?.trim()) {
      Swal.fire("Validation Error", "Meeting agenda is required.", "warning");
      return;
    }

    // Prepare cleaned payload
    const payload = {
      ...form,
      property_ids: form.property_ids?.map(p => p.value) || [],
      guests: form.guests?.map(g => g.value) || [],
      managers: form.managers?.map(m => m.value) || [],
      assistant_managers: form.assistant_managers?.map(a => a.value) || [],
      overseers: form.overseers?.map(o => o.value) || []
    };

    axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/update-meeting`, payload, {
      withCredentials: true
    }).then(res => {
      if (res.data.status) {
        Swal.fire("Success", "Meeting updated successfully.", "success");
        navigate('/overseer/meetings/list');
      } else {
        Swal.fire("Error", res.data.message || "Something went wrong.", "error");
      }
    }).catch(err => globalErrorHandler(err, navigate));
  };




  const isDisabled = form.property_ids.length === 0;


  return (
    <div className="container-fluid px-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Update Meeting</h3>
      </div>

      {!isLoading &&
        <div className="card shadow mt-4">
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-md-12">
                <label className="form-label">Meeting Title <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter meeting title" />
              </div>

              <div className="col-md-12">
                <label className="form-label">Meeting Agenda <span className="text-danger">*</span></label>
                <textarea className="form-control" rows={3} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} placeholder="Enter meeting agenda" />
              </div>

               <div className="col-md-12">
                <label className="form-label">Select Properties </label>
                <Select isMulti options={properties} value={form.property_ids} onChange={(selected) => setForm({ ...form, property_ids: selected })} placeholder="Select Properties" classNamePrefix="react-select" />
              </div>

              <div className="col-md-6">
                <label className="form-label">Select Guests </label>
                <Select isMulti options={filtered.guests} value={form.guest_ids} onChange={(selected) => setForm({ ...form, guest_ids: selected })} placeholder="Select Guests" classNamePrefix="react-select" isDisabled={isDisabled} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Overseer</label>
                <Select isMulti options={filtered.overseers} value={form.overseer_ids} onChange={(selected) => setForm({ ...form, overseer_ids: selected })} placeholder="Select Overseer(s)" classNamePrefix="react-select" isDisabled={isDisabled} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Select Manager </label>
                <Select isMulti options={filtered.managers} value={form.manager_ids} onChange={(selected) => setForm({ ...form, manager_ids: selected })} placeholder="Select Manager(s)" classNamePrefix="react-select" isDisabled={isDisabled} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Assistant Manager</label>
                <Select isMulti options={filtered.assistantManagers} value={form.assistant_manager_ids} onChange={(selected) => setForm({ ...form, assistant_manager_ids: selected })} placeholder="Select Assistant Manager(s)" classNamePrefix="react-select" isDisabled={isDisabled} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Meeting Date & Time <span className="text-danger">*</span></label>
                <Flatpickr placeholder='Enter Meeting Date & Time' className="form-control" value={form.meeting_datetime} options={{ enableTime: true, dateFormat: "Y-m-d H:i" }} onChange={([date]) => setForm({ ...form, meeting_datetime: date })} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Meeting Link</label>
                <input type="text" className="form-control" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://zoom.us/..." />
              </div>

              <div className="col-md-12">
                <label className="form-label">Meeting Notes</label>
            
             <Editor
                    apiKey={process.env.REACT_APP_TINYMCE_KEY}
                    value={form.notes}
                    onEditorChange={(content) => setForm({ ...form, notes: content })}
                    init={tinymceConfig}
                  />
              </div>

              <div className="col-12 text-end">
                <button className="btn btn-primary mt-3" onClick={updateMeeting}>Update Meeting</button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default MeetingCreate;
